import {
  useAccessToken,
  useIsTrainer,
  useIsVerifier,
  useMember,
  useTenantChecksum,
  useUser,
} from "../../core/hooks/authHooks";
import useDocumentTitle from "@jnctn/hooks/useDocumentTitle";
import useInterval from "@jnctn/hooks/useInterval";
import useGatekeeper from "@jnctn-app/core/hooks/useGatekeeper";
import useNavigateCallback from "@jnctn/hooks/useNavigateCallback";
import { ReactComponent as KeyboardIcon } from "@jnctn/icons/keyboard.svg";
import Icon from "@jnctn/design-system/Icon/Icon.component";
import { ReactComponent as BellIcon } from "@jnctn/icons/bell.svg";
import buildDocumentTitle from "@jnctn/webcore/ui-helpers/buildDocumentTitle";
import isCameraSupported from "@jnctn/webcore/ui-helpers/isCameraSupported";
import { AUTH_RETURN_PATH } from "@jnctn/webcore/storage/localStorageKeys";
import StandardButton, {
  Type,
} from "@jnctn/design-system/Button/StandardButton.component";
import FlexLayoutItem, {
  layouts,
} from "@jnctn/design-system/Flexbox/FlexLayoutItem.component";
import Link from "@jnctn/design-system/Link/Link.component";
import Text from "@jnctn/design-system/Text/Text.component";
import React, {
  lazy,
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { motion } from "motion/react";
import useOnline from "@jnctn/hooks/useOnline";
import ProfileImage from "@jnctn/design-system/ProfileImage/ProfileImage.component";
import Reader from "@jnctn/bridge/components/Reader.component";
import { resourceCache, useAsyncResource } from "use-async-resource";
import { fetchNotificationUnreadCount } from "@jnctn/api/app/fetchNotifications";
import UpdatePoint from "@jnctn/design-system/UpdatePoint/UpdatePoint.component";
import Loader from "@jnctn/design-system/Loader/Loader.component";
import { CardItem } from "@jnctn/design-system/Card/Card.component";
import AnimatePresenceWrapper from "@jnctn/design-system/AnimatePresenceWrapper/AnimatePresenceWrapper.component";
import { useAuthContext } from "../../contexts/AuthContextProvider";
import StandardPageLayout from "@jnctn-app/core/components/layout/StandardPageLayout.component";
import useLocalStorage from "@jnctn/bridge/hooks/useLocalStorage";
import { useLocation } from "react-router";

const MyQRCode = lazy(
  () => import("@jnctn-app/core/components/MyQRCode.component")
);

const TenantPicker = lazy(
  () => import("@jnctn-app/core/components/TenantPicker.component")
);

function LinkButton({
  label,
  to,
  icon,
  type,
  element,
  className,
}: {
  label: string;
  to: string;
  icon?: React.ReactElement;
  type: Type;
  element?: ReactElement<any>;
  className?: string;
}) {
  return (
    <StandardButton
      icon={icon}
      label={label}
      className={className}
      onClick={useNavigateCallback(to)}
      type={type}
      element={element}
    />
  );
}

function AuthContent() {
  const gks = useGatekeeper();
  const tenantChecksum = useTenantChecksum();

  const isTrainer = useIsTrainer();
  const isVerifier = useIsVerifier();
  const member = useMember();

  if (tenantChecksum === undefined || member === undefined) {
    return null;
  }
  const { credentialSubject } = member;

  return (
    <FlexLayoutItem
      layout={layouts.verticalCenterAll}
      className="space-y-4 w-full mt-4"
      grow={1}
    >
      <AnimatePresenceWrapper initial={false}>
        {isVerifier && gks.verify ? (
          <LinkButton
            label="Check Credentials"
            to="/verify/?from=home"
            type="primary"
            element={
              <motion.button
                initial={{ opacity: 0, scale: 0, height: 0 }}
                animate={{ opacity: 1, scale: [1, 1.2, 1], height: "auto" }}
                exit={{ opacity: 0, scale: 0, height: 0 }}
                transition={{ type: "spring", duration: 0.5 }}
              />
            }
            className="w-64"
          />
        ) : null}
      </AnimatePresenceWrapper>
      <AnimatePresenceWrapper initial={false}>
        {isTrainer && gks.assign ? (
          <LinkButton
            label="Assign Credentials"
            to="/assign/"
            type="outline"
            element={
              <motion.button
                initial={{ opacity: 0, scale: 0, height: 0 }}
                animate={{ opacity: 1, scale: [1, 1.2, 1], height: "auto" }}
                exit={{ opacity: 0, scale: 0, height: 0 }}
                transition={{ type: "spring", duration: 0.5 }}
              />
            }
            className="w-64"
          />
        ) : null}
      </AnimatePresenceWrapper>
      <FlexLayoutItem
        grow={1}
        className="bg-charcoal-200 w-full p-8"
        layout={layouts.verticalCenterAll}
      >
        <CardItem layout={layouts.verticalCenterAll} className="p-8 space-y-4">
          <TenantPicker validTenant={true} />
          <FlexLayoutItem
            layout={layouts.horizontalCenter}
            className="space-x-4"
          >
            <ProfileImage size="medium" subject={credentialSubject} />
            <Text color="primary" size="header1">
              {tenantChecksum.firstName}
              <br />
              {tenantChecksum.lastName}
            </Text>
          </FlexLayoutItem>
          <FlexLayoutItem align="center">
            <MyQRCode />
          </FlexLayoutItem>
        </CardItem>
      </FlexLayoutItem>
    </FlexLayoutItem>
  );
}

const DELAY_UNTIL_REFRESH_INDICATOR = 10000;

function NotificationIndicator() {
  const accessToken = useAccessToken();
  const tenantChecksum = useTenantChecksum();
  const [reader, reload] = useAsyncResource(
    fetchNotificationUnreadCount,
    accessToken,
    //@ts-ignore legacy type error
    tenantChecksum?.tenantId
  );

  // Here we use a ref to store a last count, this is to avoid flickering state
  const lastCountRef = useRef<number>(0);

  // Every 10s check for a new unread notification
  // TODO once we have signalR sending a general signal for each new unread notification
  // we can just subscribe here
  useInterval(
    useCallback(() => {
      try {
        // store last count
        lastCountRef.current = reader();
      } catch (e) {}

      resourceCache(fetchNotificationUnreadCount).clear();
      //@ts-ignore legacy type error
      reload(accessToken, tenantChecksum?.tenantId);
    }, [reader, accessToken, tenantChecksum, reload]),
    DELAY_UNTIL_REFRESH_INDICATOR
  );

  return (
    <Suspense
      fallback={
        <UpdatePoint
          show={lastCountRef.current > 0}
          size="small"
          className="z-1 absolute top-0 right-0"
        />
      }
    >
      <Reader reader={reader}>
        {(count) => (
          <UpdatePoint
            show={count > 0}
            size="small"
            className="z-1 absolute top-0 right-0"
          />
        )}
      </Reader>
    </Suspense>
  );
}

function Header() {
  const gks = useGatekeeper();
  const user = useUser();
  const { isAuthenticated } = useAuthContext();
  const hasUser = isAuthenticated && user !== undefined;
  return (
    <FlexLayoutItem
      layout={{ justify: "all", direction: "horizontal", align: "center" }}
      className="px-6 w-full max-w-4xl"
      shrink={0}
      align="center"
    >
      <FlexLayoutItem className="only:mx-auto only:my-0 w-20 md:w-32">
        <Icon
          iconName={hasUser ? "logo-positive" : "logo-negative"}
          className="w-36 h-20"
          size="custom"
          label="JNCTN Logo"
        />
      </FlexLayoutItem>
      {hasUser ? (
        <FlexLayoutItem className="relative">
          <Link to="/notifications/" className="w-full h-full">
            <Text color="white" size="body3">
              <BellIcon className="w-8 h-8 text-black" />
            </Text>
          </Link>
          {gks.notifications ? <NotificationIndicator /> : null}
        </FlexLayoutItem>
      ) : (
        <FlexLayoutItem />
      )}
    </FlexLayoutItem>
  );
}

function LoggedInBody() {
  return (
    <FlexLayoutItem
      className="w-full overflow-y-auto"
      grow={1}
      layout={layouts.verticalCenter}
    >
      <Suspense fallback={<Loader />}>
        <FlexLayoutItem
          layout={{
            align: "stretch",
            justify: "all",
            direction: "vertical",
          }}
          className="w-full"
          grow={1}
        >
          <AuthContent />
        </FlexLayoutItem>
      </Suspense>
    </FlexLayoutItem>
  );
}

function LoggedOutBody() {
  const isOnline = useOnline();
  const { isLoading, onSignIn } = useAuthContext();
  const location = useLocation();
  const [, setPath] = useLocalStorage(AUTH_RETURN_PATH, "/");

  useEffect(() => {
    if (!location.pathname.startsWith("/authentication/")) {
      setPath(location.pathname || "/");
    }
  }, [location.pathname, setPath]);

  if (isLoading) {
    return (
      <StandardPageLayout type="white">
        <div className="h-screen flex items-center justify-center">
          <Loader />
        </div>
      </StandardPageLayout>
    );
  }

  return (
    <>
      <Text color="white" size="header1" textAlign="center">
        Manage your
        <br />
        Credentials
      </Text>
      <FlexLayoutItem
        layout={layouts.verticalCenter}
        className="space-y-4 pb-10"
      >
        <StandardButton
          label="Log in"
          type="primary"
          className="w-64"
          onClick={onSignIn}
          disabled={!isOnline}
        />
        {isCameraSupported() ? (
          <LinkButton
            to="/scan/"
            label="Scan a QR code"
            type="transparent"
            className="w-64"
          />
        ) : (
          <LinkButton
            to="/code/"
            icon={<KeyboardIcon />}
            label="Use JNCTN code"
            type="transparent"
            className="w-64"
          />
        )}
      </FlexLayoutItem>
    </>
  );
}

function Body() {
  const user = useUser();
  const { isAuthenticated } = useAuthContext();

  if (isAuthenticated && user !== undefined) {
    return <LoggedInBody />;
  }

  return <LoggedOutBody />;
}

export default function HomePage() {
  useDocumentTitle(buildDocumentTitle(["JNCTN", "Home"]));

  return (
    <FlexLayoutItem
      className="overflow-y-hidden pt-10"
      grow={1}
      layout={{
        align: "stretch",
        justify: "all",
        direction: "vertical",
      }}
    >
      <Header />
      <Body />
    </FlexLayoutItem>
  );
}
